import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Card, CardContent, CardHeader, IconButton, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  Alert,
  CardHeaderDivider,
  ExternalLink,
  Text,
  api,
  getQueryProps,
  isDefined,
} from 'common';

import { EndpointCardDeleteDialog } from './DeleteDialog';

export interface EndpointCardProps {
  endpointId: string;
  onDelete: () => void;
}

export function EndpointCard(props: EndpointCardProps) {
  const { onDelete, endpointId } = props;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    data: fhirEndpoint,
    isPending,
    isError,
    error,
  } = useQuery({
    ...getQueryProps(api.client.Orchestrator.ApiInstanceFhir, 'findOneById', endpointId),
  });

  return (
    <Card>
      <CardHeader
        avatar={<LinkOutlinedIcon color="success" />}
        action={
          <IconButton
            aria-label="Delete Endpoint"
            size="small"
            color="error"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <DeleteOutlinedIcon />
          </IconButton>
        }
        title={isPending ? <Skeleton /> : (fhirEndpoint?.name ?? 'Unknown')}
      />
      <CardHeaderDivider />
      <EndpointCardDeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onRemove={() => {
          setIsDeleteDialogOpen(false);
          onDelete();
        }}
      />
      <CardContent component={Stack} gap={1}>
        {isError ? (
          <Alert title="Error loading FHIR endpoint" error={error} />
        ) : (
          <>
            <Text variant="body2">{isPending ? <Skeleton /> : fhirEndpoint?.description}</Text>
            {isPending ? (
              <Skeleton width="50%" />
            ) : (
              <>
                {isDefined(fhirEndpoint?.documentationLink) && (
                  <ExternalLink href={fhirEndpoint.documentationLink}>Documentation</ExternalLink>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
